import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAuth } from '../../contexts/AuthContext';
import { shForm, shJson } from './api';
import { isContratosSourceOptions, sectorIntegraContratos } from './ContratoMultiSelect';
import { getQuestionSelectionMode } from './SelecaoMultiplaCheckbox';

const TYPES = [
  { value: 'texto', label: 'Texto' },
  { value: 'status', label: 'Status' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'numero', label: 'Número' },
  { value: 'selecao', label: 'Seleção' },
  { value: 'data', label: 'Data' },
];

export default function SafetyHuddleAdminFormPage() {
  const { sectorId, templateId } = useParams();
  const navigate = useNavigate();
  const { unidadeSelecionada } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [template, setTemplate] = useState(null);
  const [optionLists, setOptionLists] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [label, setLabel] = useState('');
  const [responseType, setResponseType] = useState('texto');
  const [isIdent, setIsIdent] = useState(false);
  const [isRequired, setIsRequired] = useState(false);
  const [selectionMode, setSelectionMode] = useState('single');
  const [optionsOrigin, setOptionsOrigin] = useState('manual'); // manual | list | import | contratos
  const [manualItems, setManualItems] = useState([]);
  const [newItem, setNewItem] = useState('');
  const [optionListId, setOptionListId] = useState('');
  const [tipoOpcoes, setTipoOpcoes] = useState('outro');
  const [importFile, setImportFile] = useState(null);
  const [importListName, setImportListName] = useState('Colaboradores');

  const isContratosSector = sectorIntegraContratos(template?.sector);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await shJson(`/api/safety-huddle/templates/${templateId}`, unidadeSelecionada);
      setTemplate(data);
      const lists = await shJson(
        `/api/safety-huddle/sectors/${sectorId}/option-lists`,
        unidadeSelecionada
      );
      setOptionLists(lists.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [templateId, sectorId, unidadeSelecionada]);

  useEffect(() => {
    load();
  }, [load]);

  const resetDialog = () => {
    setLabel('');
    setResponseType('texto');
    setIsIdent(false);
    setIsRequired(false);
    setSelectionMode(isContratosSector ? 'multiple' : 'single');
    setOptionsOrigin(isContratosSector ? 'contratos' : 'manual');
    setManualItems([]);
    setNewItem('');
    setOptionListId('');
    setTipoOpcoes('outro');
    setImportFile(null);
    setImportListName('Colaboradores');
  };

  const openDialog = () => {
    resetDialog();
    setOpen(true);
  };

  const addManualItem = () => {
    const v = newItem.trim();
    if (!v) return;
    if (manualItems.some((x) => x.toLowerCase() === v.toLowerCase())) {
      setNewItem('');
      return;
    }
    setManualItems((prev) => [...prev, v]);
    setNewItem('');
  };

  const createQuestion = async () => {
    if (!label.trim()) return;
    setSaving(true);
    setError('');
    try {
      let origin = optionsOrigin;
      let listId = optionListId ? Number(optionListId) : null;
      let tipo = tipoOpcoes;

      if (responseType === 'selecao' && origin === 'import') {
        if (!importFile) {
          setError('Selecione um arquivo .xlsx/.csv para importar.');
          setSaving(false);
          return;
        }
        const fd = new FormData();
        fd.append('file', importFile);
        fd.append('name', importListName.trim() || 'Colaboradores');
        fd.append('tipoOpcoes', 'pessoas');
        fd.append('mode', 'create');
        const created = await shForm(
          `/api/safety-huddle/sectors/${sectorId}/option-lists/import`,
          unidadeSelecionada,
          fd
        );
        listId = created.id;
        origin = 'list';
        tipo = created.tipoOpcoes || 'pessoas';
        setOptionLists((prev) => [created, ...prev.filter((x) => x.id !== created.id)]);
      }

      if (responseType === 'selecao' && origin === 'contratos') {
        tipo = 'contratos';
      }
      if (responseType === 'selecao' && origin === 'list') {
        const chosen = optionLists.find((l) => String(l.id) === String(listId));
        if (chosen?.tipoOpcoes) tipo = chosen.tipoOpcoes;
      }

      const body = {
        label: label.trim(),
        responseType,
        isSubjectIdentifier: isIdent,
        required: isRequired,
        selectionMode: responseType === 'selecao' ? selectionMode : undefined,
        optionsOrigin: responseType === 'selecao' ? origin : undefined,
        optionListId: responseType === 'selecao' && origin === 'list' ? listId : undefined,
        tipoOpcoes: responseType === 'selecao' ? tipo : undefined,
        items: responseType === 'selecao' && origin === 'manual' ? manualItems : undefined,
        options:
          responseType === 'selecao' && origin === 'contratos'
            ? { source: 'contratos' }
            : responseType === 'selecao' && origin === 'manual'
              ? { source: 'manual', items: manualItems }
              : responseType === 'selecao' && origin === 'list'
                ? { source: 'list', optionListId: listId }
                : null,
      };

      await shJson(`/api/safety-huddle/templates/${templateId}/questions`, unidadeSelecionada, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setOpen(false);
      resetDialog();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const setAsIdentifier = async (qid) => {
    try {
      await shJson(`/api/safety-huddle/questions/${qid}`, unidadeSelecionada, {
        method: 'PUT',
        body: JSON.stringify({ isSubjectIdentifier: true }),
      });
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const removeQuestion = async (qid) => {
    try {
      await shJson(`/api/safety-huddle/questions/${qid}`, unidadeSelecionada, {
        method: 'DELETE',
      });
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const describeQuestion = (q) => {
    const bits = [];
    if (q.responseType !== 'selecao') bits.push(`Tipo: ${q.responseType}`);
    else {
      const mode =
        getQuestionSelectionMode(q) === 'multiple' ? 'múltipla escolha' : 'única (dropdown)';
      const opts = q.options;
      if (isContratosSourceOptions(opts)) {
        bits.push(`Tipo: Seleção · contratos · ${mode}`);
      } else if (opts && typeof opts === 'object' && opts.source === 'list') {
        bits.push(
          `Tipo: Seleção · lista «${opts.listName || opts.optionListId}» · ${mode}`
        );
      } else {
        const n = Array.isArray(opts)
          ? opts.length
          : Array.isArray(opts?.items)
            ? opts.items.length
            : 0;
        bits.push(`Tipo: Seleção · ${mode} · ${n} opção(ões)`);
      }
    }
    if (q.required) bits.push('obrigatória');
    return bits.join(' · ');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/safety-huddle/setores/${sectorId}`)}
        sx={{ mb: 2 }}
      >
        Voltar ao setor
      </Button>

      <Typography variant="h5" fontWeight={700} color="primary.main">
        Configurar — {template?.name}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Em Seleção, escolha a origem das opções (digitar, lista salva, planilha ou contratos).
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button startIcon={<AddIcon />} variant="contained" onClick={openDialog}>
          Nova pergunta
        </Button>
      </Box>

      <List>
        {(template?.questions || []).map((q) => (
          <ListItem
            key={q.id}
            sx={{ border: '1px solid rgba(25,118,210,0.12)', borderRadius: 2, mb: 1 }}
            secondaryAction={
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {!q.isSubjectIdentifier && (
                  <Button size="small" onClick={() => setAsIdentifier(q.id)}>
                    Identificador
                  </Button>
                )}
                <Button size="small" color="error" onClick={() => removeQuestion(q.id)}>
                  <DeleteOutlineIcon fontSize="small" />
                </Button>
              </Box>
            }
          >
            <ListItemText
              primary={
                <Typography fontWeight={600}>
                  {q.sortOrder}. {q.label}
                  {q.required ? (
                    <Typography component="span" color="error.main" fontWeight={700}>
                      {' '}
                      *
                    </Typography>
                  ) : null}
                  {q.isSubjectIdentifier ? ' · ID' : ''}
                </Typography>
              }
              secondary={describeQuestion(q)}
            />
          </ListItem>
        ))}
      </List>

      {!template?.questions?.length && (
        <Alert severity="info">Nenhuma pergunta — adicione ao menos uma para usar o formulário.</Alert>
      )}

      <Button
        sx={{ mt: 2 }}
        variant="outlined"
        onClick={() => navigate(`/safety-huddle/setores/${sectorId}/formularios/${templateId}`)}
      >
        Abrir formulário
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nova pergunta</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Enunciado"
            fullWidth
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Tipo</InputLabel>
            <Select
              label="Tipo"
              value={responseType}
              onChange={(e) => {
                const v = e.target.value;
                setResponseType(v);
                if (v === 'selecao' && isContratosSector) {
                  setOptionsOrigin('contratos');
                  setSelectionMode('multiple');
                }
              }}
            >
              {TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {responseType === 'selecao' && (
            <>
              <FormControl fullWidth margin="dense" size="small">
                <InputLabel>Origem das opções</InputLabel>
                <Select
                  label="Origem das opções"
                  value={optionsOrigin}
                  onChange={(e) => {
                    const v = e.target.value;
                    setOptionsOrigin(v);
                    if (v === 'contratos') {
                      setTipoOpcoes('contratos');
                      setSelectionMode('multiple');
                    } else if (v === 'import') {
                      setTipoOpcoes('pessoas');
                      setSelectionMode('multiple');
                    }
                  }}
                >
                  <MenuItem value="manual">Digitar agora</MenuItem>
                  <MenuItem value="list">Usar lista salva</MenuItem>
                  <MenuItem value="import">Importar planilha (.xlsx / .csv)</MenuItem>
                  <MenuItem value="contratos">Contratos da unidade</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth margin="dense" size="small">
                <InputLabel>Modo de seleção</InputLabel>
                <Select
                  label="Modo de seleção"
                  value={selectionMode}
                  onChange={(e) => setSelectionMode(e.target.value)}
                >
                  <MenuItem value="single">Dropdown (única escolha)</MenuItem>
                  <MenuItem value="multiple">Checkbox (múltipla escolha)</MenuItem>
                </Select>
              </FormControl>

              {optionsOrigin === 'manual' && (
                <Box sx={{ mt: 1 }}>
                  <FormControl fullWidth margin="dense" size="small">
                    <InputLabel>Tipo das opções</InputLabel>
                    <Select
                      label="Tipo das opções"
                      value={tipoOpcoes}
                      onChange={(e) => setTipoOpcoes(e.target.value)}
                    >
                      <MenuItem value="outro">Outro</MenuItem>
                      <MenuItem value="setores">Setores</MenuItem>
                      <MenuItem value="pessoas">Pessoas / colaboradores</MenuItem>
                    </Select>
                  </FormControl>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Novo item"
                      value={newItem}
                      onChange={(e) => setNewItem(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addManualItem();
                        }
                      }}
                    />
                    <Button variant="outlined" onClick={addManualItem} disabled={!newItem.trim()}>
                      +
                    </Button>
                  </Box>
                  <List dense sx={{ maxHeight: 180, overflow: 'auto' }}>
                    {manualItems.map((it, idx) => (
                      <ListItem
                        key={`${it}-${idx}`}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => setManualItems((p) => p.filter((_, i) => i !== idx))}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText primary={it} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {optionsOrigin === 'list' && (
                <FormControl fullWidth margin="dense" size="small">
                  <InputLabel>Lista salva</InputLabel>
                  <Select
                    label="Lista salva"
                    value={optionListId}
                    onChange={(e) => setOptionListId(e.target.value)}
                  >
                    {optionLists.map((l) => (
                      <MenuItem key={l.id} value={String(l.id)}>
                        {l.name} ({l.items.length} itens)
                      </MenuItem>
                    ))}
                  </Select>
                  {!optionLists.length && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Nenhuma lista — use «Minhas listas» no setor ou importe uma planilha.
                    </Alert>
                  )}
                </FormControl>
              )}

              {optionsOrigin === 'import' && (
                <Box sx={{ mt: 1.5 }}>
                  <TextField
                    margin="dense"
                    label="Nome da lista a criar"
                    fullWidth
                    size="small"
                    value={importListName}
                    onChange={(e) => setImportListName(e.target.value)}
                  />
                  <Button variant="outlined" component="label" sx={{ mt: 1 }}>
                    Escolher planilha
                    <input
                      hidden
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    />
                  </Button>
                  {importFile && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      {importFile.name}
                    </Typography>
                  )}
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Use coluna <strong>Nome</strong> (ou a 1ª coluna). Cria uma lista reutilizável
                    (tipo pessoas) e vincula a esta pergunta.
                  </Alert>
                </Box>
              )}

              {optionsOrigin === 'contratos' && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  Opções = contratos da unidade (busca dinâmica).
                </Alert>
              )}
            </>
          )}

          <FormControlLabel
            control={<Checkbox checked={isRequired} onChange={(e) => setIsRequired(e.target.checked)} />}
            label="Pergunta obrigatória"
          />
          <FormControlLabel
            control={<Checkbox checked={isIdent} onChange={(e) => setIsIdent(e.target.checked)} />}
            label="Identificador do registro (cruzamento no dashboard)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={saving || !label.trim()} onClick={createQuestion}>
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
